import React, { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { compose } from 'recompose';
import { asBrowserDate } from '@/utilities/convertToISO';
import OrderPODModal from '@/components/CarrierAccountingOrders/modals/OrderPODModal';
import { hasAnySubmitted210 } from '@/utilities/orderful';
import { OnwardBreadcrumbActive, OnwardBreadcrumbInactive, OnwardBreadcrumbSpacer } from '@/components/Breadcrumbs';
import { TextField, Grid, Accordion, AccordionDetails, AccordionSummary, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { PrimaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { css } from '@emotion/react';
import { GetApp as DownloadIcon, Add as AddIcon } from '@material-ui/icons';
import ExportSplitButton from '../ExportSplitButton';
import { INVOICE_STATUS_DISPLAY_MAPPINGS } from './constants';
import { NetsuiteLink } from '@/components/NetsuiteLink';
import { BillcomLink } from '@/components/BillcomLink';
import Table from './Table';
import Totals from './Totals';
import RevertDialog from './RevertDialog';
import RevertIncompleteBanner from './RevertIncompleteBanner';
import DownstreamRecords from './DownstreamRecords';
import SupplementalCharges from './SupplementalCharges';
import { Context, ContextProvider } from './store';
import { H1, H2, Body1, Body2, StatusBadge } from './blocks';
import StickyFooter from '@/components/StickyFooter';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import * as ROUTES from '@/constants/routes';

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const Stat = ({ label, value, ...rest }) => {
    return (
        <Grid container direction="row" {...rest}>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: content;
                    justify-content: center;
                    margin-right: 12px;
                `}
            >
                <Body1>{label}</Body1>
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: content;
                    justify-content: center;
                `}
            >
                <Body1>{value}</Body1>
            </Grid>
        </Grid>
    );
};

export const Invoice = ({ context }) => {
    const { state, loading, callbacks } = useContext(context);
    const navigate = useNavigate();
    const [selectedExportIndex, setSelectedExportIndex] = useState(0);
    // Both custom formats are receivable-only; the row generators filter non-receivables away, so
    // without this check the option would render and then produce a header-only file.
    const isReceivable = ['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(state?.invoice?.type);

    // With one option the button keeps its original "Export" label.
    const exportOptions = useMemo(() => {
        if (state?.isElectroluxExportEnabled && isReceivable) {
            return ['Default Export', 'Electrolux CSV', 'Electrolux TXT'];
        }
        if (state?.masterbrandExportEnabled && isReceivable) {
            return ['Default Export', 'MasterBrand CSV'];
        }
        if (state?.pelotonExportEnabled && isReceivable) {
            return ['Default Export', 'Peloton CSV'];
        }
        return ['Export'];
    }, [state?.isElectroluxExportEnabled, state?.masterbrandExportEnabled, state?.pelotonExportEnabled, isReceivable]);

    // Dispatch on the label rather than the index: index 1 is Electrolux CSV or MasterBrand CSV
    // depending on which circle the client carries.
    const handleExport = (index) => {
        switch (exportOptions[index]) {
            case 'Electrolux CSV':
                return callbacks.exportElectroluxCsv();
            case 'Electrolux TXT':
                return callbacks.exportElectroluxTxt();
            case 'MasterBrand CSV':
                return callbacks.exportMasterbrandCsv();
            case 'Peloton CSV':
                return callbacks.exportPelotonCsv();
            default:
                return callbacks.exportCsv();
        }
    };

    if (loading.init || !state.invoice) {
        return null;
    }

    return (
        <>
            <Grid
                direction="column"
                container
                css={css`
                    width: 100%;
                    height: 200%;
                `}
            >
                <Grid
                    direction="row"
                    container
                    css={css`
                        align-items: center;
                        background: white;
                        padding: 12px 30px;
                        box-shadow: 0px 0px 0px 1px rgba(63, 63, 68, 0.05), 0px 1px 3px rgba(63, 63, 68, 0.15);
                        border-bottom: 1px solid #ececec;
                    `}
                >
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <Grid
                            direction="row"
                            container
                            css={css`
                                align-items: center;
                            `}
                        >
                            {state.breadcrumbs.map((crumb, idx) => (
                                <>
                                    {crumb.active ? (
                                        <OnwardBreadcrumbActive disabled>{crumb.label}</OnwardBreadcrumbActive>
                                    ) : (
                                        <OnwardBreadcrumbInactive
                                            onClick={() => {
                                                navigate(crumb.route);
                                            }}
                                        >
                                            {crumb.label}
                                        </OnwardBreadcrumbInactive>
                                    )}
                                    {idx < state.breadcrumbs.length - 1 && <OnwardBreadcrumbSpacer />}
                                </>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                        `}
                    >
                        <PrimaryButton
                            css={css`
                                height: 40px;
                                align-self: flex-end;
                                & > div:first-of-type {
                                    display: flex !important;
                                    flex-direction: row !important;
                                    align-items: center !important;
                                    justify-content: center !important;
                                    gap: 6px !important;
                                }
                            `}
                            onClick={callbacks.downloadAllPODs}
                            loading={loading.downloadingPODs}
                            disabled={!state.hasPODsAvailable || loading.downloadingPODs}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                PODs
                                <DownloadIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} />
                            </span>
                        </PrimaryButton>
                    </Grid>
                    {state?.canEditSupplemental && (
                        <Grid
                            direction="column"
                            container
                            css={css`
                                flex: 0;
                                flex-basis: 0;
                                margin-left: 12px;
                            `}
                        >
                            <PrimaryButton
                                css={css`
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                disabled={loading.supplemental}
                                onClick={() =>
                                    callbacks.addSupplementalCharge({
                                        code: '',
                                        description: '',
                                        rate: 0,
                                        quantity: 1,
                                        qbo_tag: '',
                                    })
                                }
                            >
                                Add Supplemental Charge
                            </PrimaryButton>
                        </Grid>
                    )}
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                        `}
                    >
                        <ExportSplitButton
                            options={exportOptions}
                            selectedIndex={selectedExportIndex}
                            onSelect={setSelectedExportIndex}
                            onExport={handleExport}
                            loading={loading.export}
                            buttonCss={css`
                                margin-left: 12px;
                                height: 40px;
                                align-self: flex-end;
                                justify-content: center;
                                text-align: center;
                            `}
                        />
                    </Grid>
                    {state?.isAdmin && state?.invoice?.status === 'APPROVED' && !state?.revertIncomplete ? (
                        <Grid
                            direction="column"
                            container
                            css={css`
                                flex: 0;
                                flex-basis: 0;
                            `}
                        >
                            <PrimaryButton
                                css={css`
                                    margin-left: 12px;
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    white-space: nowrap;
                                `}
                                onClick={() => callbacks?.refreshIntegrations?.()}
                                loading={loading.refreshing}
                            >
                                Refresh
                            </PrimaryButton>
                        </Grid>
                    ) : null}

                    <Grid
                        direction="column"
                        container
                        css={css`
                            margin-left: 12px;
                            flex: 0;
                            flex-basis: 0;
                        `}
                    >
                        {state?.invoice?.status === 'DRAFT' && state?.isAdmin && (
                            <PrimaryButton
                                css={css`
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                onClick={() => callbacks.setSubmitted()}
                                loading={loading.update}
                            >
                                Submit
                            </PrimaryButton>
                        )}
                        {state?.invoice?.status === 'UNPAID' && state?.isAdmin && (
                            <PrimaryButton
                                css={css`
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                onClick={() => callbacks.setApproved()}
                                loading={loading.update}
                            >
                                Approve
                            </PrimaryButton>
                        )}
                        {state?.invoice?.status === 'APPROVED' && state?.isAdmin && (
                            <PrimaryButton
                                css={css`
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                onClick={() => callbacks.setPaid()}
                                loading={loading.update}
                            >
                                Mark as Paid
                            </PrimaryButton>
                        )}
                    </Grid>
                    {state?.canRevert && (
                        <Grid
                            direction="column"
                            container
                            css={css`
                                margin-left: 12px;
                                flex: 0;
                                flex-basis: 0;
                            `}
                        >
                            <PrimaryButton
                                red
                                css={css`
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                onClick={() => callbacks.openRevert()}
                                disabled={loading.revert}
                            >
                                Revert Invoice
                            </PrimaryButton>
                        </Grid>
                    )}
                </Grid>

                {(state?.revertIncomplete || state?.revertError) && (
                    <RevertIncompleteBanner
                        invoice={state.invoice}
                        error={state.revertError}
                        onRetry={() => callbacks.openRevert()}
                    />
                )}

                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        padding: 0 40px;
                        margin-bottom: 30px;
                        margin-top: 30px;
                    `}
                >
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            justify-content: center;
                        `}
                    >
                        <Grid container item direction="row">
                            <Grid
                                direction="column"
                                container
                                css={css`
                                    flex: 0;
                                    flex-basis: content;
                                    justify-content: center;
                                    margin-right: 12px;
                                `}
                            >
                                <H1>
                                    {(() => {
                                        const base = `Invoice ${state.invoice?.invoice_number}${
                                            state.invoiceTypeLabel ? ` · ${state.invoiceTypeLabel}` : ''
                                        } (${
                                            state.invoice.type === 'INTERNAL'
                                                ? state.invoice.partner_client?.business_name
                                                : state.invoice.client?.business_name
                                        })`;
                                        // INTERNAL already shows partner_client in the parenthetical above; skip to avoid duplication.
                                        const partnerName =
                                            state.invoice.type !== 'INTERNAL'
                                                ? state.invoice.partner_client?.business_name
                                                : null;
                                        const partnerLabel = ['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(
                                            state.invoice.type
                                        )
                                            ? 'Carrier'
                                            : 'Shipper';
                                        return partnerName ? `${base} · ${partnerLabel}: ${partnerName}` : base;
                                    })()}
                                </H1>
                            </Grid>
                            {state.invoice?.status === 'DRAFT' ? (
                                <Grid
                                    direction="column"
                                    container
                                    css={css`
                                        flex: 0;
                                        flex-basis: content;
                                        justify-content: center;
                                    `}
                                >
                                    <Body1
                                        css={css`
                                            color: #d23e3e;
                                        `}
                                    >{`Edits due by ${
                                        state.editsBy ? dateNumeric.format(state.editsBy) : '--'
                                    }`}</Body1>
                                </Grid>
                            ) : null}
                        </Grid>
                    </Grid>

                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            justify-content: center;
                        `}
                    >
                        <StatusBadge error={state?.invoice?.status !== 'PAID' && state?.invoice?.status !== 'APPROVED'}>
                            <H2
                                css={css`
                                    white-space: nowrap;
                                `}
                            >
                                {INVOICE_STATUS_DISPLAY_MAPPINGS[state?.invoice?.status] || '--'}
                            </H2>
                        </StatusBadge>
                    </Grid>
                </Grid>

                {state.missingPOD ? (
                    <Grid
                        container
                        direction="row"
                        css={css`
                            padding: 0 40px;
                            margin-bottom: 20px;
                        `}
                    >
                        <Grid
                            container
                            item
                            css={css`
                                padding: 18px 24px;
                                border-radius: 5px;
                                background: #d23e3e;
                            `}
                        >
                            <Body2
                                css={css`
                                    color: ${colors.white.primary};
                                `}
                            >
                                Invoices with missing PODs will not be paid until resolved
                            </Body2>
                        </Grid>
                    </Grid>
                ) : null}

                <Grid
                    direction="row"
                    container
                    css={css`
                        padding: 0 40px;
                        margin-bottom: 20px;
                    `}
                >
                    <Accordion
                        css={css`
                            width: 100%;
                        `}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <H2>Billing Comments ({state.invoice?.notes?.length || 0})</H2>
                        </AccordionSummary>
                        <AccordionDetails
                            css={css`
                                display: flex;
                                flex-direction: column;
                                padding: 0 1rem 0rem 1rem;
                                margin-bottom: 0px;
                                justify-content: space-between;
                            `}
                        >
                            {(state?.invoice?.notes || [])?.map((note, i) => (
                                <Grid
                                    container
                                    direction="row"
                                    key={i}
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        padding: '10px',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            width: 100%;
                                        `}
                                    >
                                        <Grid
                                            container
                                            direction="row"
                                            css={css`
                                                margin-bottom: 8px;
                                            `}
                                        >
                                            <Body1
                                                css={css`
                                                    font-size: 12px;
                                                    color: #4c4c4c;
                                                `}
                                            >
                                                {note.user?.username} - {new Date(note.created_at).toLocaleString()}
                                            </Body1>
                                        </Grid>
                                        <Grid container direction="row">
                                            <Body1>{note.note}</Body1>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                            <TextField
                                multiline
                                charLimit={200}
                                maxRows={2}
                                value={state.newComment || ''}
                                css={css`
                                    width: 100%;
                                    height: 100px;
                                    justify-content: center;
                                `}
                                variant="outlined"
                                placeholder="Add a comment..."
                                onChange={(e) => {
                                    callbacks.setNewComment(e.target.value);
                                }}
                            />
                            <PrimaryButton
                                css={css`
                                    margin-bottom: 10px;
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                disabled={loading.notes || !state?.newComment}
                                onClick={() => {
                                    callbacks.saveComments();
                                }}
                            >
                                Submit
                            </PrimaryButton>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid
                    container
                    direction="row"
                    css={css`
                        padding: 0 40px;
                        margin-bottom: 20px;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            width: 100%;

                            padding: 18px;
                            background: ${colors.white.primary};

                            box-shadow: 0px 1px 3px 0px #3f3f4426;
                            box-shadow: 0px 0px 0px 1px #3f3f440d;
                        `}
                    >
                        <Stat
                            label="Issued:"
                            value={
                                state?.invoice?.created_at
                                    ? dateNumeric.format(asBrowserDate(state?.invoice?.created_at))
                                    : '--'
                            }
                            css={css`
                                margin-bottom: 4px;
                            `}
                        />
                        <Stat
                            label="Due:"
                            value={
                                state?.invoice?.due_date
                                    ? dateNumeric.format(asBrowserDate(state?.invoice?.due_date))
                                    : '--'
                            }
                            css={css`
                                margin-bottom: 4px;
                            `}
                        />
                        <Stat label="Description:" value={state?.invoice?.description || '--'} />
                        {state?.canViewIntegrations && (
                            <>
                                <Stat
                                    label="NetSuite:"
                                    value={<NetsuiteLink invoice={state?.invoice} />}
                                    css={css`
                                        margin-top: 4px;
                                    `}
                                />
                                <Stat
                                    label="Bill.com:"
                                    value={<BillcomLink invoice={state?.invoice} />}
                                    css={css`
                                        margin-top: 4px;
                                    `}
                                />
                                <DownstreamRecords invoice={state?.invoice} />
                                {hasAnySubmitted210(
                                    state?.invoice,
                                    state?.orders.map((order) => order.order)
                                ) ? (
                                    <Stat
                                        label="Orderful:"
                                        value={`${state.orderfulMissing} of ${state.orderfulTotal} orders not submitted`}
                                        css={css`
                                            margin-top: 4px;
                                        `}
                                    />
                                ) : null}
                            </>
                        )}
                    </Grid>
                </Grid>

                <Grid
                    item
                    css={css`
                        overflow: hidden;
                        flex-grow: 1;
                        padding: 0 40px;
                        margin-bottom: 20px;
                        min-height: 300px;
                        max-height: 500px;
                    `}
                >
                    <Table state={state} loading={loading} callbacks={callbacks} />
                </Grid>

                <SupplementalCharges />

                <Totals state={state} />
            </Grid>

            <OrderPODModal
                order={state.viewPod}
                callbacks={{
                    onClose: () => {
                        callbacks.viewOrderPod(null);
                    },
                }}
            />
            {state?.invoiceIdx >= 0 && (
                <StickyFooter
                    styles={{
                        margin: 0,
                        boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <Grid container direction="row" justifyContent="center">
                        <PrimaryButton disabled={state.invoiceIdx === 0} onClick={() => callbacks.goToNextInvoice(-1)}>
                            <Grid item>
                                <span>
                                    <ChevronLeftIcon />
                                </span>
                                <span>Previous Invoice</span>
                            </Grid>
                        </PrimaryButton>

                        <PrimaryButton
                            disabled={state.invoiceIdx === state.allInvoices.length - 1}
                            onClick={() => callbacks.goToNextInvoice(1)}
                            css={css`
                                margin-left: 25px;
                            `}
                        >
                            <Grid item>
                                <span>Next Invoice</span>
                                <span>
                                    <ChevronRightIcon />
                                </span>
                            </Grid>
                        </PrimaryButton>
                    </Grid>
                </StickyFooter>
            )}

            <RevertDialog
                key={state?.revertPreview ? 'open' : 'closed'}
                preview={state?.revertPreview}
                submitting={loading.revert}
                onClose={() => callbacks.closeRevert()}
                onConfirm={(reason) => callbacks.confirmRevert(reason)}
            />

            <Snackbar
                open={!!state?.revertNotification}
                autoHideDuration={6000}
                onClose={() => callbacks.dismissRevertNotification()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    onClose={() => callbacks.dismissRevertNotification()}
                    severity={state?.revertNotification?.severity || 'info'}
                >
                    {state?.revertNotification?.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} context={Context} />
        </ContextProvider>
    );

export default compose(withContext)(Invoice);
